<template>
	<div v-if="isShow" class="user-profile-card" :style="{ left: pos.x + 'px', top: pos.y + 'px' }" @click.stop>
		<div class="user-profile-card__panel">
			<div class="user-profile-card__toolbar">
				<div class="user-profile-card__title">个人资料</div>
				<button type="button" class="user-profile-card__close" @click="close">
					<i class="el-icon-close"></i>
				</button>
			</div>
			<profile-summary
				:avatar-name="displayName"
				:avatar-url="form.headImageThumb"
				:avatar-size="72"
				:title="displayName"
			>
				<template #avatar>
					<div class="avatar-upload-wrap">
						<file-upload
							class="avatar-uploader"
							action="/image/upload?thumbSize=20"
							:isPermanent="true"
							:showLoading="true"
							:multiple="false"
							:maxSize="maxSize"
							:fileTypes="['image/jpeg', 'image/png', 'image/jpg', 'image/webp']"
							@before="onAvatarUploadBefore"
							@success="onUploadSuccess"
							@fail="onAvatarUploadFail"
						>
							<head-image
								class="avatar-uploader__image"
								:size="72"
								:url="displayAvatarUrl"
								:online="Boolean(userInfo.online)"
								radius="50%"
								:name="displayName"
								@load="onAvatarImageLoad"
							></head-image>
						</file-upload>
						<div class="avatar-upload-wrap__mask">
							<i class="el-icon-camera"></i>
							<span>更换</span>
						</div>
					</div>
				</template>
				<template #extra>
					<div class="account-row" :title="userInfo.userName">
						<span class="account-row__label">账号</span>
						<span class="account-row__value">{{ userInfo.userName }}</span>
						<button type="button" class="account-row__copy" title="复制账号" @click="copyUserName">
							<i class="el-icon-copy-document"></i>
						</button>
					</div>
				</template>
			</profile-summary>

			<div class="user-profile-card__form">
				<div class="form-field">
					<label class="form-field__label" for="me-info-nick-name">昵称</label>
					<el-input
						id="me-info-nick-name"
						v-model.trim="form.nickName"
						maxlength="32"
						show-word-limit
						placeholder="请输入昵称"
						:disabled="saving"
						@blur="saveProfile('nickName')"
						@keyup.enter.native="saveProfile('nickName')"
					></el-input>
				</div>
			</div>

			<div v-if="saving" class="user-profile-card__footer">
				<span v-if="saving" class="user-profile-card__saving">
					<i class="el-icon-loading"></i>
					正在保存
				</span>
			</div>
		</div>
	</div>
</template>

<script>
import FileUpload from './FileUpload.vue';
import HeadImage from './HeadImage.vue';
import ProfileSummary from './ProfileSummary.vue';
import { isPersistedProtectedAssetPath } from '../../api/protectedAssetUrl.js';

export default {
	name: 'UserProfileCard',
	components: {
		FileUpload,
		HeadImage,
		ProfileSummary
	},
	data() {
		return {
			form: {},
			isShow: false,
			maxSize: 5 * 1024 * 1024,
			pos: {
				x: 0,
				y: 0
			},
			saving: false,
			pendingSavePromise: null,
			avatarPreviewUrl: '',
			retiredAvatarPreviewUrl: '',
			viewportGap: 12
		};
	},
	mounted() {
		window.addEventListener('resize', this.onViewportChange);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.onViewportChange);
		this.revokeAvatarUrl(this.avatarPreviewUrl);
		this.revokeAvatarUrl(this.retiredAvatarPreviewUrl);
	},
	methods: {
		sanitizeAvatar(userInfo) {
			const nextUser = userInfo || {};
			if (!isPersistedProtectedAssetPath(nextUser.headImage)) {
				nextUser.headImage = '';
			}
			if (!isPersistedProtectedAssetPath(nextUser.headImageThumb)) {
				nextUser.headImageThumb = '';
			}
			return nextUser;
		},
		resetForm() {
			const userInfo = this.userStore.userInfo || {};
			this.form = this.sanitizeAvatar(JSON.parse(JSON.stringify(userInfo)));
		},
		open(position = {}) {
			if (!this.pendingSavePromise) {
				this.resetForm();
			}
			this.pos.x = Number(position.x) || this.viewportGap;
			this.pos.y = Number(position.y) || this.viewportGap;
			this.isShow = true;
			this.$nextTick(() => {
				this.adjustPos();
			});
		},
		close() {
			this.isShow = false;
		},
		onViewportChange() {
			if (!this.isShow) {
				return;
			}
			this.adjustPos();
		},
		adjustPos() {
			const panel = this.$el;
			if (!panel) {
				return;
			}
			const rect = panel.getBoundingClientRect();
			const maxY = window.innerHeight - rect.height - this.viewportGap;
			const maxX = window.innerWidth - rect.width - this.viewportGap;
			this.pos.y = Math.max(this.viewportGap, Math.min(this.pos.y, maxY));
			this.pos.x = Math.max(this.viewportGap, Math.min(this.pos.x, maxX));
		},
		onAvatarUploadBefore(file) {
			this.revokeAvatarUrl(this.avatarPreviewUrl);
			this.revokeAvatarUrl(this.retiredAvatarPreviewUrl);
			this.retiredAvatarPreviewUrl = '';
			this.avatarPreviewUrl = URL.createObjectURL(file);
		},
		onUploadSuccess(data) {
			this.form.headImage = data.originUrl;
			this.form.headImageThumb = data.thumbUrl || data.originUrl;
			const previewUrl = this.avatarPreviewUrl;
			const remoteUrl = this.form.headImageThumb;
			if (previewUrl && remoteUrl) {
				this.$assetResolver.resolve(remoteUrl).then(() => {
					if (this.avatarPreviewUrl !== previewUrl) {
						return;
					}
					this.retiredAvatarPreviewUrl = previewUrl;
					this.avatarPreviewUrl = '';
				}).catch(() => {
					// Keep the local preview visible; the persisted URL can recover on the next render.
				});
			}
			this.saveProfile('avatar');
		},
		onAvatarUploadFail() {
			if (!this.avatarPreviewUrl) {
				return;
			}
			this.retiredAvatarPreviewUrl = this.avatarPreviewUrl;
			this.avatarPreviewUrl = '';
		},
		onAvatarImageLoad(source) {
			if (!this.retiredAvatarPreviewUrl || source === this.retiredAvatarPreviewUrl) {
				return;
			}
			this.revokeAvatarUrl(this.retiredAvatarPreviewUrl);
			this.retiredAvatarPreviewUrl = '';
		},
		revokeAvatarUrl(url) {
			if (url && url.startsWith('blob:')) {
				URL.revokeObjectURL(url);
			}
		},
		copyUserName() {
			const userName = this.userInfo.userName;
			if (!userName) {
				return;
			}
			if (navigator.clipboard && window.isSecureContext) {
				navigator.clipboard.writeText(userName).then(() => {
					this.$message.success(`内容'${userName}'已复制到剪贴板`);
				}).catch(() => {
					this.$message.error('复制失败，请手动复制');
				});
				return;
			}
			this.$message.error('复制失败，请手动复制');
		},
		isProfileChanged(payload) {
			return payload.nickName !== (this.userInfo.nickName || '') ||
				payload.headImage !== (this.userInfo.headImage || '') ||
				payload.headImageThumb !== (this.userInfo.headImageThumb || '');
		},
		saveProfile(reason = 'profile') {
			if (this.saving) {
				return this.pendingSavePromise;
			}
			const nickName = String(this.form.nickName || '').trim();
			if (!nickName) {
				this.$message.error('请输入昵称');
				this.form.nickName = this.userInfo.nickName || '';
				return Promise.resolve(false);
			}
			const payload = this.sanitizeAvatar({
				...this.userInfo,
				...this.form,
				nickName,
				signature: this.userInfo.signature || ''
			});
			if (!this.isProfileChanged(payload)) {
				this.form.nickName = payload.nickName;
				return Promise.resolve(false);
			}
			this.saving = true;
			this.pendingSavePromise = this.$http({
				url: '/user/update',
				method: 'put',
				data: payload
			}).then(() => {
				const currentUser = this.sanitizeAvatar({
					...(this.userStore.userInfo || {}),
					...payload
				});
				this.userStore.setUserInfo(currentUser);
				this.form = this.sanitizeAvatar(JSON.parse(JSON.stringify(currentUser)));
				this.groupStore.syncGroupMemberFromUser(currentUser);
				this.chatStore.updateChatFromUser(currentUser);
				this.$message.success(reason === 'avatar' ? '头像已更新' : '昵称已更新');
			}).finally(() => {
				this.saving = false;
				this.pendingSavePromise = null;
			});
			return this.pendingSavePromise;
		}
	},
	computed: {
		userInfo() {
			return this.userStore.userInfo || {};
		},
		displayName() {
			return this.form.nickName || this.userInfo.nickName || this.userInfo.userName || '未设置昵称';
		},
		displayAvatarUrl() {
			return this.avatarPreviewUrl || this.form.headImageThumb;
		},
	}
};
</script>

<style scoped lang="scss">
.user-profile-card {
	position: fixed;
	z-index: 10020;
	width: min(360px, calc(100vw - 24px));
	max-height: calc(100vh - 24px);
	overflow: hidden;

	&__panel {
		padding: 14px;
		border-radius: 14px;
		background: var(--im-surface-panel-strong);
		border: 1px solid var(--im-surface-panel-border);
		box-shadow: 0 28px 56px -36px rgba(var(--im-black-rgb), 0.46);
		backdrop-filter: blur(14px);
	}

	&__toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		margin-bottom: 12px;
	}

	&__title {
		font-size: 14px;
		font-weight: 600;
		color: var(--im-text-color);
	}

	&__close {
		width: 28px;
		height: 28px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border: none;
		border-radius: 8px;
		background: transparent;
		color: var(--im-text-color-light);
		cursor: pointer;
		transition: background 0.2s ease, color 0.2s ease;

		&:hover {
			background: var(--im-icon-hover-bg);
			color: var(--im-text-color);
		}
	}

	&__form {
		margin-top: 14px;
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	&__footer {
		margin-top: 14px;
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 8px;
	}

	&__saving {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		margin-right: auto;
		font-size: 12px;
		color: var(--im-text-color-light);
	}
}

.avatar-upload-wrap {
	position: relative;
	width: 72px;
	height: 72px;

	.avatar-uploader {
		--width: 72px;
		position: relative;
		display: block;
		width: var(--width);
		height: var(--width);
		border-radius: 50%;
		overflow: hidden;

		:deep(.el-upload) {
			width: var(--width);
			height: var(--width);
			box-sizing: border-box;
			display: block;
			position: relative;
			border-radius: 50%;
			overflow: hidden;
			border: 1px solid var(--im-control-border);
			transition: border-color 0.2s ease, opacity 0.2s ease;

			&:hover {
				border-color: var(--im-color-primary);
				opacity: 0.94;
			}
		}

		:deep(.file-upload__loading) {
			width: 100%;
			height: 100%;
			border-radius: 50%;
		}
	}

	&__mask {
		position: absolute;
		inset: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 2px;
		border-radius: 50%;
		background: rgba(var(--im-black-rgb), 0.48);
		color: var(--im-text-color-white);
		font-size: 11px;
		pointer-events: none;
		opacity: 0;
		transition: opacity 0.2s ease;

		i {
			font-size: 16px;
		}
	}

	&:hover {
		.avatar-upload-wrap__mask {
			opacity: 1;
		}
	}
}

.account-row {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 10px;
	border-radius: 10px;
	background: var(--im-control-soft-bg);
	border: 1px solid var(--im-control-border);

	&__label {
		flex-shrink: 0;
		font-size: 12px;
		color: var(--im-text-color-light);
	}

	&__value {
		flex: 1;
		min-width: 0;
		font-size: 12px;
		color: var(--im-text-color);
		font-family: 'Courier New', monospace;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__copy {
		width: 24px;
		height: 24px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border: none;
		border-radius: 6px;
		background: transparent;
		color: var(--im-color-primary);
		cursor: pointer;
		transition: background 0.2s ease;

		&:hover {
			background: rgba(var(--im-primary-rgb), 0.08);
		}
	}
}

.form-field {
	display: flex;
	flex-direction: column;
	gap: 6px;

	&__label {
		font-size: 12px;
		font-weight: 600;
		color: var(--im-text-color-light);
	}
}

@media (max-width: 640px) {
	.user-profile-card {
		width: calc(100vw - 16px);
		max-height: calc(100vh - 16px);

		&__panel {
			padding: 12px;
		}

		&__footer {
			flex-wrap: wrap;

			:deep(.el-button) {
				flex: 1 1 calc(50% - 4px);
				margin-left: 0;
			}
		}
	}
}
</style>
